/**
 * Pure helpers for reading and shaping the asset group `assets` list.
 *
 * Every state-shaping helper takes the current (frozen) assets and returns the next assets without mutating its
 * inputs. Helpers return the same array reference when nothing changes. The store actions in `assetGroup.store.ts`
 * are thin wrappers around these.
 *
 * This module must not import the asset group actions or `core.stores`: the store imports it, and `core.stores`
 * creates the store, so either import would create a cycle that leaves the store undefined at load time.
 */

import _ from 'lodash';
import { PATH_SEPARATOR } from '@/assetGroupEditor/assetGroup.constants';
import { API_TYPES } from '@/main/app.constants';
import type {
  AssetAncestor,
  AssetGroupAsset,
  AssetGroupChild,
  AssetGroupInputParameter,
} from '@/assetGroupEditor/assetGroup.types';

/** The fields of a calculation child that are applied when adding or updating it. */
export type AssetGroupCalculationChild = Pick<AssetGroupChild, 'name' | 'formula' | 'parameters' | 'manuallyAdded'>;

/**
 * A column added to every asset. Placeholder columns only carry a name and the user that added them; calculation
 * columns also carry their formula.
 */
export type NewAssetGroupAttribute = Pick<AssetGroupChild, 'name' | 'manuallyAdded'> &
  Partial<Pick<AssetGroupChild, 'columnType' | 'formula' | 'parameters'>>;

export function getPath(item: AssetAncestor) {
  if (item?.assetPath) {
    return item.assetPath;
  } else {
    return _.join(_.map(_.filter(item?.ancestors, { type: API_TYPES.ASSET }), 'name'), PATH_SEPARATOR);
  }
}

export function getNameWithoutPath(item: AssetGroupAsset) {
  return _.indexOf(item.name, PATH_SEPARATOR) < 0
    ? item.name
    : item.name.substring(_.lastIndexOf(item.name, PATH_SEPARATOR) + 1);
}

export function findChild(asset: AssetGroupAsset, column: string): AssetGroupChild | undefined {
  return _.find(asset.children, (child) => child?.name === column);
}

export function getColumnsHelper(assets: AssetGroupAsset[]): AssetGroupChild[] {
  return _.chain(assets)
    .flatMap('children')
    .uniqBy('name')
    .sortBy((child) => _.toLower(child.name))
    .value();
}

export function getParametersUsedByFormula(
  parameters: AssetGroupInputParameter[],
  formula: string,
): AssetGroupInputParameter[] {
  const usedParameters = formula.match(/\$\w+/g)?.map((s) => s.substring(1).toLowerCase());

  return _.filter(
    parameters,
    (param: AssetGroupInputParameter) => _.indexOf(usedParameters, param.name.toLowerCase()) > -1,
  );
}

export function getOneLevelUpPath(assets: AssetGroupAsset[]): AssetGroupAsset[] {
  return _.map(assets, (current) => {
    const startingPath = getPath(current);
    if (current.path) {
      // trim the path to "cut off" the path we've already extracted
      const remainingPath = startingPath.substring(0, startingPath.indexOf(current.path) - 1);
      const path = remainingPath.substring(_.lastIndexOf(remainingPath, PATH_SEPARATOR) + 1);
      return _.assign({}, current, {
        path: `${path}${PATH_SEPARATOR}${current.path}`,
      });
    } else {
      const path = startingPath.substring(_.lastIndexOf(startingPath, PATH_SEPARATOR) + 1);
      return _.assign({}, current, { path });
    }
  });
}

export function findExistingAsset(assets: AssetGroupAsset[], newAsset: AssetGroupAsset): AssetGroupAsset | null {
  return _.chain(assets)
    .filter((asset) => {
      if (getNameWithoutPath(asset) === getNameWithoutPath(newAsset)) {
        return newAsset.assetPath === asset.assetPath;
      }
      return false;
    })
    .head()
    .value() as AssetGroupAsset;
}

function replaceAssetAt(assets: AssetGroupAsset[], index: number, asset: AssetGroupAsset): AssetGroupAsset[] {
  const next = [...assets];
  next[index] = asset;
  return next;
}

/**
 * Adds a calculation child to the asset named `asset.name`, replacing an existing child of the same name.
 * Only the parameters actually referenced by the formula are kept.
 */
export function addCalculationChildToAsset(
  assets: AssetGroupAsset[],
  asset: AssetGroupAsset,
  { name, formula, parameters, manuallyAdded = false }: AssetGroupCalculationChild,
): AssetGroupAsset[] {
  const index = assets.findIndex((storeAsset) => storeAsset.name === asset.name);
  if (index < 0) {
    return assets;
  }

  const actuallyUsedParameters = getParametersUsedByFormula(parameters, formula);
  const children = [...(assets[index].children ?? [])];
  const existingChildIndex = children.findIndex((child) => child?.name === name);
  // manually added attributes that get added for the first time should be treated like brand new children
  // for children that are being updated with a new signal we need to keep track so we can update the
  // underlying formula not the asset
  if (existingChildIndex > -1) {
    const [existingChild] = children.splice(existingChildIndex, 1);
    children.push({ ...existingChild, formula, parameters: actuallyUsedParameters });
  } else {
    children.push({
      type: '',
      columnType: 'Calculation',
      formula,
      parameters: actuallyUsedParameters,
      name,
      manuallyAdded,
    });
  }

  return replaceAssetAt(assets, index, { ...assets[index], ...asset, children });
}

/**
 * Adds or replaces a "pass-thru" (non-calculation) child on the matching asset. The first item ever assigned to a
 * child is recorded as its `originalMapping`.
 */
export function addChildToAsset(
  assets: AssetGroupAsset[],
  asset: AssetGroupAsset,
  child: AssetGroupChild,
): AssetGroupAsset[] {
  const index = assets.findIndex(
    (current) => getNameWithoutPath(current) === asset.name && current.assetPath === asset.assetPath,
  );
  if (index < 0) {
    return assets;
  }

  // NOTE: the existing child is looked up on the asset that was passed in, not on the stored asset
  const existingChild = _.find(asset.children, (assetChild) => child.name === assetChild.name);
  const updatedChild: AssetGroupChild = { ...existingChild, ...child };
  // we only want to track the "original" mapping - the first ever assigned
  if (!existingChild) {
    updatedChild.originalMapping = _.get(child, 'parameters[0].item.id');
  }

  return replaceAssetAt(assets, index, {
    ...assets[index],
    ...asset,
    children: [..._.filter(assets[index].children, (storeChild) => child.name !== storeChild.name), updatedChild],
  });
}

function addChild(assets: AssetGroupAsset[], asset: AssetGroupAsset, child: AssetGroupChild): AssetGroupAsset[] {
  return child.columnType === 'Calculation'
    ? addCalculationChildToAsset(assets, asset, child)
    : addChildToAsset(assets, asset, child);
}

/** Renames the asset that has the same name and path as `original`. */
export function updateAssetName(
  assets: AssetGroupAsset[],
  original: AssetGroupAsset,
  newName: string,
): AssetGroupAsset[] {
  return _.map(assets, (asset) =>
    asset.name === original.name && getPath(asset) === getPath(original) ? { ...asset, name: newName } : asset,
  );
}

/** Adds every calculation column of the group to `asset`, so a newly added row computes the same columns. */
function addAllCalculatedChildrenToAsset(assets: AssetGroupAsset[], asset: AssetGroupAsset): AssetGroupAsset[] {
  const existingAsset = findExistingAsset(assets, asset);
  if (!existingAsset) {
    return assets;
  }

  const calculationColumns = _.filter(getColumnsHelper(assets), { columnType: 'Calculation' });
  return _.reduce(
    calculationColumns,
    (next, calcColumn) => {
      // We need to modify the params so the formulas properly map to the correct signal - if it's a previously
      // saved assetGroupColumnBased column we don't want to use an existing id as that would be bad
      const parameters = _.map(calcColumn.parameters, (param) =>
        param.assetGroupColumnBased ? _.omit(param, 'item.id') : param,
      ) as AssetGroupInputParameter[];

      return addCalculationChildToAsset(next, existingAsset, { ...calcColumn, parameters });
    },
    assets,
  );
}

/**
 * Adds a manually created row. It gets an empty slot for every non-calculation column that has no item assigned yet,
 * plus every calculation column.
 */
export function addEmptyAsset(
  assets: AssetGroupAsset[],
  asset: AssetGroupAsset,
  currentUserId: string,
): AssetGroupAsset[] {
  let next = [...assets, asset];

  if (findExistingAsset(next, asset)) {
    const emptyColumns = _.filter(
      getColumnsHelper(next),
      (child) => child.columnType !== 'Calculation' && _.isEmpty(child.parameters),
    );
    next = _.reduce(
      emptyColumns,
      (current, emptyColumn) =>
        addChildToAsset(current, asset, {
          name: emptyColumn.name,
          manuallyAdded: currentUserId,
          parameters: [],
          columnType: 'Item',
          formula: '',
        }),
      next,
    );
  }

  return addAllCalculatedChildrenToAsset(next, asset);
}

/**
 * Adds an asset (or merges its children into a matching existing asset). When several assets end up with the same
 * name, each is prefixed with as much of its path as required to make the names unique.
 */
export function addAsset(assets: AssetGroupAsset[], asset: AssetGroupAsset): AssetGroupAsset[] {
  let next = assets;
  const existingAsset = findExistingAsset(next, asset);
  if (existingAsset && !_.isEmpty(existingAsset)) {
    next = _.reduce(asset.children, (current, child) => addChild(current, existingAsset, child), next);
  } else {
    // adds a new asset entry. this will add all the "children" of the assets as well as calculation children
    next = [...next, { ...asset, children: [] }];
    const addedAsset = findExistingAsset(next, asset);
    if (addedAsset) {
      next = _.reduce(asset.children, (current, child) => addChildToAsset(current, addedAsset, child), next);
    }
    // we also want to add all the calculations to this newly added asset!
    next = addAllCalculatedChildrenToAsset(next, asset);
  }

  // to ensure unique names we prefix asset names with as much of their path as required to make them "unique"
  let assetsWithSameName = _.chain(next)
    .filter((storeAsset) => getNameWithoutPath(storeAsset) === asset.name)
    .map((sameNameAsset) => _.omit(sameNameAsset, 'path'))
    .value() as AssetGroupAsset[];

  if (_.size(assetsWithSameName) > 1) {
    while (_.size(assetsWithSameName) > 0) {
      assetsWithSameName = getOneLevelUpPath(assetsWithSameName);
      const paths = _.map(assetsWithSameName, 'path');
      // now every asset has a "path" property - make sure all those paths are unique:
      assetsWithSameName = _.filter(assetsWithSameName, (current) => {
        const dupePaths = _.filter(paths, (path) => path === current.path);
        if (_.size(dupePaths) === 1) {
          const originalName = getNameWithoutPath(current);
          next = updateAssetName(next, current, `${current.path}${PATH_SEPARATOR}${originalName}`);
          return false;
        }
        return true;
      });
    }
  }

  return next;
}

/**
 * Clears the item id of the parameter that pointed at `parameterItemName` (a removed column) so the calculation does
 * not look as if it "reverted" to the signal the column was based on.
 */
export function updateChildParameter(
  assets: AssetGroupAsset[],
  asset: AssetGroupAsset,
  child: AssetGroupChild,
  parameterItemName: string,
): AssetGroupAsset[] {
  const parameterIndex = _.findIndex(child.parameters, (param) => param.item.name === parameterItemName);
  if (parameterIndex < 0 || !child.parameters[parameterIndex].assetGroupColumnBased) {
    return assets;
  }

  const otherParameters = _.filter(child.parameters, (_param, index) => index !== parameterIndex);
  const paramToUpdate = _.cloneDeep(child.parameters[parameterIndex]);
  paramToUpdate.item.id = '';
  return addCalculationChildToAsset(assets, asset, { ...child, parameters: [...otherParameters, paramToUpdate] });
}

/** Points the child named `name` back at `newItemId`. */
export function restoreChild(
  assets: AssetGroupAsset[],
  asset: AssetGroupAsset,
  name: string,
  newItemId: string,
): AssetGroupAsset[] {
  const child = findChild(asset, name);
  if (!child) {
    return assets;
  }

  const updatedChild = _.cloneDeep(child);
  if (!_.isEmpty(updatedChild.parameters) && updatedChild.parameters[0].item) {
    updatedChild.parameters[0].item.id = newItemId;
  }
  return addChildToAsset(assets, asset, updatedChild);
}

/** Adds a new column to every asset. */
export function addAttribute(assets: AssetGroupAsset[], newChild: NewAssetGroupAttribute): AssetGroupAsset[] {
  const { formula, parameters = [] } = newChild;
  // Placeholder columns intentionally have no formula, parameters, or column type until an item is assigned
  const child = (
    formula ? { ...newChild, parameters: getParametersUsedByFormula(parameters, formula) } : newChild
  ) as AssetGroupChild;
  return _.map(assets, (asset) => ({ ...asset, children: [...(asset.children ?? []), child] }));
}

/** Renames a column on every asset and updates the calculations that reference it. */
export function updateColumnName(assets: AssetGroupAsset[], originalName: string, newName: string): AssetGroupAsset[] {
  // be very careful changing the order of things here! we need to rename the children BEFORE we update the
  // calculation dependencies or we overwrite the required calculation updates!
  const renamed: AssetGroupAsset[] = _.map(assets, (asset) => ({
    ...asset,
    children: _.map(asset.children, (child) => (child.name === originalName ? { ...child, name: newName } : child)),
  }));

  // Iterate over the renamed assets, but always apply each update to the most current assets
  return _.reduce(
    renamed,
    (next, asset) =>
      _.reduce(
        asset.children ?? [],
        (current, child) => {
          if (child.columnType !== 'Calculation') {
            return current;
          }
          const parameterIndex = _.findIndex(
            child.parameters,
            (param) => param.item.name === originalName && !!param.assetGroupColumnBased,
          );
          if (parameterIndex < 0) {
            return current;
          }
          const otherParameters = _.filter(child.parameters, (_param, index) => index !== parameterIndex);
          const paramToUpdate = _.cloneDeep(child.parameters[parameterIndex]);
          paramToUpdate.item.name = newName;
          return addCalculationChildToAsset(current, asset, {
            ...child,
            parameters: [...otherParameters, paramToUpdate],
          });
        },
        next,
      ),
    renamed,
  );
}

/** Removes the child named `child.name` from the matching asset. */
export function removeChildFromAsset(
  assets: AssetGroupAsset[],
  asset: AssetGroupAsset,
  child: AssetGroupChild,
): AssetGroupAsset[] {
  const existingAsset = findExistingAsset(assets, asset);
  if (!existingAsset) {
    return assets;
  }

  const index = assets.indexOf(existingAsset);
  return replaceAssetAt(assets, index, {
    ...existingAsset,
    children: _.filter(existingAsset.children, (childEntry) => childEntry.name !== child.name),
  });
}

/** Removes the column named `name` from every asset. */
export function removeAttribute(assets: AssetGroupAsset[], name: string): AssetGroupAsset[] {
  return _.map(assets, (asset) => ({
    ...asset,
    children: _.filter(asset.children, (child) => child.name !== name),
  }));
}
